// Stable test hooks — the ONE way a shared runtime component exposes itself
// to the UI acceptance-test executor (backend/app/services/ui_testing/).
//
// Test cases name MODEL things (object, column, action, page block), never
// CSS or visible text, so they survive a restyle and can be validated against
// the models before a run. Every surface renders these same components, so a
// hook added here makes the Run view, the published site and the downloaded
// runtime testable at once. Attributes only — no visual or behavioural effect.
//
// Vocabulary (keep in step with ui_testing/steps.py `HOOKS`):
//   screen   <obj>                 a transaction / visual / dashboard / report / page host
//   form     <form_no>             one form of a transaction screen
//   field    <form key>            a field wrapper (col: db_name, state attrs below)
//   action   <name>                a toolbar or action button
//   row      <index>               a list-grid row
//   nav      <obj>                 a run-view menu entry
//   block    <type>:<id>           a smart-page section / block
//   msg      error|warning|success a message the user is shown
//   group    <group name>          a collapsible field group's toggle (aria-expanded)
//   wizard   <obj>                 a Transaction Wizard runner (obj_type "U")
//   wizard-step  <step id>         the step on screen (kind: data-tw-kind)
//   wizard-next / wizard-back  1   the footer's Next / Back
//   wizard-tool  <label>           a wizard / step tool
//   wizard-scan-input  <step id>   a scan step's wedge input (type a code + Enter)
//
// Additional attributes (packages/page-runtime/src/uda):
//   action uda_details             a line's Details chip (opens the drawer,
//                                  data-tw-dialog="uda_details"; flags carry
//                                  data-tw-flag=<key>, an n/a cell data-tw-na)
//   action uda-setup               Customize → Additional attributes (the bar
//                                  entry and a field panel's link)
//   screen uda-setup, action uda-step:<n>, action uda-publish
//                                  the setup screen (uda/designer); Publish's
//                                  dialog confirms with action uda-publish-confirm
//
// The Customize and setup-screen hooks are listed as DATA in
// testHookVocabulary.json (re-exported below as TEST_HOOK_VOCABULARY): the
// acceptance-case validator (ui_testing/schema.py) accepts exactly those names,
// and test_tw_hooks.mjs fails when a listed hook stops being emitted.

import TEST_HOOK_VOCABULARY from './testHookVocabulary.json'

export { TEST_HOOK_VOCABULARY }

export type TwHookKind =
  | 'screen' | 'form' | 'field' | 'action' | 'row' | 'nav' | 'block' | 'msg' | 'group'
  | 'wizard' | 'wizard-step' | 'wizard-next' | 'wizard-back' | 'wizard-tool' | 'wizard-scan-input'
  // A row of the approvals inbox (value = instance id; extra obj / status / kind).
  | 'approval'

export type TwHookProps = Record<`data-tw-${string}`, string>

/** `{ 'data-tw-<kind>': value }` plus optional extra `data-tw-*` attributes.
 *  A blank value yields no attribute, so a component without an identity
 *  never claims a wrong one. */
export function tw(
  kind: TwHookKind,
  value: string | number | null | undefined,
  extra?: Record<string, string | number | boolean | null | undefined>,
): TwHookProps {
  const out: TwHookProps = {}
  const v = value == null ? '' : String(value).trim()
  if (v) out[`data-tw-${kind}`] = v
  if (extra) {
    for (const [k, x] of Object.entries(extra)) {
      if (x === null || x === undefined || x === false || x === '') continue
      // Trimmed: a CHAR key column arrives space-padded to its width, and a
      // case must be able to name the record by its value.
      out[`data-tw-${k}`] = x === true ? '1' : String(x).trim()
    }
  }
  return out
}
